import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ChargeCalculationsPage.css";
import {
  chargeCalculationsService,
  ChargeCalculationSummary,
} from "../services/charge-calculations.service";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import { formatCurrency, formatDate } from "../utils/format";
import { isHttpError } from '../utils/error';

const ChargeCalculationsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const [calculations, setCalculations] = useState<ChargeCalculationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createYear, setCreateYear] = useState<number>(new Date().getFullYear());
  const [createNotes, setCreateNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedColoc) {
        setCalculations([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await chargeCalculationsService.listByColoc(selectedColoc.id);
        setCalculations(data);
      } catch (err) {
        setError("Échec du chargement des calculs de charges");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedColoc]);

  const handleCreate = async () => {
    if (!selectedColoc) return;

    try {
      setCreating(true);
      setCreateError(null);
      const calc = await chargeCalculationsService.create(
        selectedColoc.id,
        createYear,
        createNotes || undefined
      );
      setShowCreateModal(false);
      setCreateNotes("");
      navigate(`/charges/${calc.id}`);
    } catch (err) {
      if (isHttpError(err, 409)) {
        setCreateError(`Un calcul existe déjà pour l'année ${createYear}.`);
      } else {
        setCreateError("Erreur lors de la création du calcul.");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string, year: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(`Supprimer le calcul de charges ${year} ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await chargeCalculationsService.deleteCalculation(id);
      setCalculations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      if (isHttpError(err, 400)) {
        setError("Ce calcul est finalisé. Repassez-le en brouillon pour le supprimer.");
      } else {
        setError("Erreur lors de la suppression du calcul.");
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

  const statusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return "Brouillon";
      case "finalized":
        return "Finalisé";
      default:
        return status;
    }
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des calculs de charges...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!selectedColoc) {
    return (
      <div className="no-coloc-message">
        Veuillez sélectionner une colocation pour voir les charges.
      </div>
    );
  }

  return (
    <div className="charges-container">
      <h2>Charges prévisionnelles pour {selectedColoc.name}</h2>

      <div className="charges-actions">
        <div />
        <button className="action-button" onClick={() => setShowCreateModal(true)}>
          Nouveau calcul
        </button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Nouveau calcul de charges</h3>
            <div className="form-group">
              <label htmlFor="calc-year">Année</label>
              <select
                id="calc-year"
                value={createYear}
                onChange={(e) => setCreateYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="calc-notes">Notes (optionnel)</label>
              <textarea
                id="calc-notes"
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                rows={3}
                placeholder="Notes ou commentaires..."
              />
            </div>
            {createError && <div className="modal-error">{createError}</div>}
            <div className="modal-actions">
              <button
                className="action-button cancel-button"
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
              >
                Annuler
              </button>
              <button
                className="action-button"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? "Création..." : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculations List */}
      <div className="charges-list">
        <div className="charges-header">
          <div className="charge-column col-year">Année</div>
          <div className="charge-column col-total">Total</div>
          <div className="charge-column col-status">Statut</div>
          <div className="charge-column col-date">Créé le</div>
          <div className="charge-column col-modified">Modifié le</div>
          <div className="charge-column col-actions">Actions</div>
        </div>

        {calculations.length > 0 ? (
          calculations.map((calc) => (
            <div
              key={calc.id}
              className="charge-row"
              onClick={() => navigate(`/charges/${calc.id}`)}
            >
              <div className="charge-column col-year">
                <strong>{calc.year}</strong>
              </div>
              <div className="charge-column col-total">
                {formatCurrency(calc.totalAmount)}
              </div>
              <div className="charge-column col-status">
                <span className={`status-badge status-${calc.status}`}>
                  {statusLabel(calc.status)}
                </span>
              </div>
              <div className="charge-column col-date">
                {calc.dateCreated ? formatDate(calc.dateCreated) : "-"}
              </div>
              <div className="charge-column col-modified">
                {calc.dateModified ? formatDate(calc.dateModified) : "-"}
              </div>
              <div className="charge-column col-actions">
                <button
                  className="action-button edit-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/charges/${calc.id}`);
                  }}
                >
                  Voir
                </button>
                {calc.status !== "finalized" && (
                  <button
                    className="action-button delete-button"
                    onClick={(e) => handleDelete(calc.id, calc.year, e)}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-charges-message">
            Aucun calcul de charges. Cliquez sur "Nouveau calcul" pour commencer.
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargeCalculationsPage;
